<template>
  <div :id="'product-carousel-' + id" class="product-carousel-container" :class="[containerMargin && 'container-margin']">
    <h2 v-if="title" class="light product-page heading new-pdp-title ctf-heading">{{ title }}</h2>
    <slot name="subtitle"></slot>
    <div class="carousel-container">
      <div class="carousel" :style="[carouselPadding ? { padding: `0px ${carouselPadding}px` } : {}]" ref="productsEl">
        <div v-if="products.length > 0" class="carousel-spacer"></div>
        <div v-for="(product, index) in products" :key="product.id" class="carousel-product" :index="index">
          <Product
            :product="product"
            :show-variations="showVariations"
            :space-bottom="false"
            :badge="!onSaleFilter"
            :open-in-new-tab="openInNewTab"
            @click="onProductClick"
            @favoriteClick="onFavoriteClicked"
          >
            <template v-if="showAddToCartBtn" v-slot:responsiveCartButton>
              <AddToCartButton
                :products="parseProduct(product)"
                :show-loader="!animateCartBtn"
                :button-classes="['outlined', 'coral']"
                :scroll-first="false"
                class="add-to-cart-button"
                :animate="animateCartBtn"
                @added="onProductAddedToCart(product)"
              >
                <span class="af af-plus"></span>&nbsp;<span class="af af-cart"></span>
              </AddToCartButton>
            </template>

            <template v-slot:customTitle>
              <slot name="customProductTitle" :productTitle="product.title"></slot>
            </template>
          </Product>
        </div>
      </div>
      <div
        v-if="scrollRightEnabled || scrollLeftEnabled"
        class="carousel-button-next"
        :class="{ disabled: !scrollRightEnabled, 'small-arrow': smallArrows }"
        @click="scrollRight"
      >
        <span class="af af-chevron-right"></span>
      </div>
      <div
        v-if="scrollRightEnabled || scrollLeftEnabled"
        class="carousel-button-prev"
        :class="{ disabled: !scrollLeftEnabled, 'small-arrow': smallArrows }"
        @click="scrollLeft"
      >
        <span class="af af-chevron-left"></span>
      </div>
    </div>
  </div>
</template>

<script>
import { watch, ref, onMounted, onUnmounted, toRefs } from 'vue';
import useResize from '@/compositions/useResize.js';
import Product from '@/components/commons/ProductNA.vue';
import Tracking from './Tracking.js';
import AddToCartButton from '@/components/commons/AddToCartButton.vue';

const slideBreakpointKeys = ['slidesPerView', 'spaceBetween'];

let carouselIdInc = 0;

const validateSlideBreakpoints = (breakpoints) =>
  Object.keys(breakpoints).every((key) => slideBreakpointKeys.every((expectedKey) => expectedKey in breakpoints[key]));

export default {
  name: 'ProductCarousel',
  components: {
    Product,
    AddToCartButton,
  },
  emits: ['productAddedToCart', 'carouselClicked'],
  props: {
    title: String,
    collection: Object,
    products: Array,
    onSaleFilter: Boolean,
    toSendEvent: {
      type: Boolean,
      default: false,
    },
    containerMargin: {
      type: Boolean,
      default: true,
    },
    slidesPerView: {
      type: Number,
      default: 4.75,
    },
    slideBreakpoints: {
      type: Object,
      validator: (breakpoints) => validateSlideBreakpoints(breakpoints),
    },
    showVariations: {
      type: Boolean,
      default: false,
    },
    spaceBetween: {
      type: Number,
      default: 80,
    },
    smallArrows: {
      type: Boolean,
      default: false,
    },
    carouselPadding: {
      type: Number,
      default: null,
    },
    showAddToCartBtn: {
      type: Boolean,
      default: false,
    },
    animateCartBtn: {
      type: Boolean,
      default: false,
    },
    openInNewTab: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      id: null,
      toShow: true,
      loaded: false,
    };
  },
  computed: {
    productsWithoutVariation() {
      if (this.products) {
        return this.products.map((product) => ({ ...product, variations: null }));
      }
      return null;
    },
  },
  setup(props) {
    const { width } = useResize();
    const scrollLeftEnabled = ref(false);
    const scrollRightEnabled = ref(false);
    const productsEl = ref(null);
    const { toSendEvent } = toRefs(props);

    let trackingObserverAttached = false;

    onMounted(() => {
      updateArrows();

      if (productsEl.value) {
        productsEl.value.addEventListener('scroll', updateArrows);

        // track analytics once the user actually interacts with the carousel
        if (toSendEvent.value) productsEl.value.addEventListener('scroll', attachTrackingObserver);
      }
    });

    onUnmounted(() => {
      if (productsEl.value) {
        productsEl.value.removeEventListener('scroll', updateArrows);
        if (toSendEvent.value) productsEl.value.removeEventListener('scroll', attachTrackingObserver);
      }
    });

    function updateArrows() {
      if (productsEl.value) {
        scrollLeftEnabled.value = productsEl.value.scrollLeft > 0;
        scrollRightEnabled.value = productsEl.value.scrollLeft + productsEl.value.offsetWidth < productsEl.value.scrollWidth;
      }
    }

    watch(width, updateArrows);

    function attachTrackingObserver() {
      if (trackingObserverAttached) return; // if observer is already attached, do nothing.

      let lastTrackedIndex = -1;

      function handleIntersection(entries) {
        entries.forEach((entry) => {
          const index = parseInt(entry.target.getAttribute('index'));

          // avoid tracking same product twice.
          if (index === lastTrackedIndex) return;

          if ((!entry.isIntersecting && entry.boundingClientRect.x <= 0) || (entry.isIntersecting && entry.intersectionRect.left <= 0)) {
            const payload = {
              product_id: props.products[index].id || props.products[index].hashKey,
              carousel_index: index,
            };

            if (props.title) payload.carousel_name = props.title;

            lastTrackedIndex = index;
            Tracking.track('product_carousel_index', payload);
          }
        });
      }

      const observer = new IntersectionObserver(handleIntersection, {
        root: productsEl.value,
      });

      const products = productsEl?.value.querySelectorAll('.carousel-product');
      products?.forEach((product) => observer.observe(product));

      trackingObserverAttached = true;
    }

    function scrollLeft() {
      if (productsEl.value) {
        productsEl.value.scrollBy({
          left: -300,
          behavior: 'smooth',
        });
      }
    }

    function scrollRight() {
      if (productsEl.value) {
        productsEl.value.scrollBy({
          left: 300,
          behavior: 'smooth',
        });
      }
    }

    return {
      updateArrows,
      scrollLeftEnabled,
      scrollRightEnabled,
      scrollLeft,
      scrollRight,
      productsEl,
    };
  },
  mounted() {
    this.id = carouselIdInc++;
  },
  methods: {
    onProductAddedToCart(product) {
      this.$emit('productAddedToCart', product);
      this.$emit('carouselClicked', { clicked: 'addToCartClicked' });
    },

    parseProduct(product) {
      return [{ id: product.id, quantity: product.packagedItemQuantity }];
    },

    onProductClick() {
      this.$emit('carouselClicked', { clicked: 'productClicked' });
    },
    onFavoriteClicked(payload) {
      this.$emit('carouselClicked', { clicked: 'favouriteProductClicked' });
    },
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';
@import '@/pixel/style/mixins';

.product-carousel-container {
  min-height: 220px;

  h1 {
    padding-bottom: 15px;
  }
  .subtitle {
    line-height: 150%;
    font-size: 16px;
    color: #626262;
    text-transform: none;
    letter-spacing: 0px;

    .link {
      color: var(--coral);
      text-decoration: underline;
    }
  }

  .carousel-container {
    position: relative;
    margin-top: var(--space-4);
  }

  .carousel {
    display: flex;
    width: inherit;
    overflow-x: scroll;
    white-space: nowrap;
    scrollbar-width: none;

    @mixin escapeResponsiveContainerPadding;

    .carousel-spacer {
      min-width: 20px;

      @media (min-width: 768px) {
        min-width: 24px;
      }

      @media (min-width: 1200px) {
        min-width: 40px;
      }
      @media (min-width: 1800px) {
        min-width: 80px;
      }
    }

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .carousel-product {
    position: relative;
    white-space: normal;
    margin-right: var(--space-10);
    width: 230px;
    flex-basis: 230px;
    flex-shrink: 0;
    flex-grow: 0;
  }

  &.container-margin {
    margin-top: 40px;
    margin-bottom: 40px;
  }

  .new-pdp-title {
    text-transform: uppercase;
    text-align: left;
  }

  .product-container {
    .favorite {
      top: 4px;
      right: 4px;
    }
  }

  .carousel-button-next,
  .carousel-button-prev {
    position: absolute;
    visibility: hidden;
    opacity: 0;
    transition: visibility 0s linear 100ms, opacity 100ms ease-in;
    background-image: none;
    display: flex;
    font-size: 1em;
    width: 32px;
    height: 32px;
    top: 25%;
    padding: 8px;
    color: var(--link-color);
    margin-top: inherit;
    background-color: $white;
    justify-content: center;
    box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.2);
    border-radius: 50%;
    cursor: pointer;

    &.small-arrow {
      width: 22px;
      height: 22px;
    }

    @media (max-width: calc($tablet-breakpoint - 1px)) {
      display: none;
    }

    .af {
      align-self: center;
      padding: 10px;
    }
  }

  .carousel-button-next {
    right: 16px;
  }

  .carousel-button-prev {
    left: 16px;
  }

  /* purgecss ignore */
  &:hover {
    .carousel-button-next,
    .carousel-button-prev {
      visibility: visible;
      opacity: 1;
      transition: visibility 0s linear, opacity 200ms ease-out;

      &.disabled {
        pointer-events: initial;
        opacity: 50%;
      }
    }
  }

  .add-to-cart-button {
    button,
    .button {
      display: flex;
      background-color: var(--white);
    }

    button {
      font-size: 14px;
      padding: 8px 12px;

      @media (max-width: calc($mobile-breakpoint - 1px)) {
        font-size: 1em;
        padding: 10px 12px;
      }
    }

    .button {
      justify-content: center;
      padding: 6px 12px;

      @media (max-width: calc($mobile-breakpoint - 1px)) {
        padding: 9px 12px;
      }
    }
  }
  .product-page.heading {
    .new-pdp-title {
      font-size: 32px;
      color: #626262;
      text-transform: none;
      letter-spacing: 0px;
      margin-bottom: var(--space-4);
    }
  }
}
</style>
